import React, { useState, useEffect } from 'react';
import { FiX, FiStar, FiClock } from 'react-icons/fi';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db, auth } from '../../firebase';

function LeaderboardModal({ quizId, quizName, onClose }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        const participantsQuery = query(
          collection(db, 'public-quiz-participants'),
          where('battleId', '==', quizId),
          where('completed', '==', true),
          orderBy('score', 'desc'),
          orderBy('completedAt', 'asc')
        );
        const participantsSnapshot = await getDocs(participantsQuery);
        
        const uniqueUserScores = new Map();
        participantsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.userId) {
            const existingEntry = uniqueUserScores.get(data.userId);
            
            if (!existingEntry || 
                (data.score > existingEntry.score || 
                 (data.score === existingEntry.score && 
                  (data.completedAt?.toMillis() - data.startedAt?.toMillis()) < 
                  (existingEntry.completedAt?.toMillis() - existingEntry.startedAt?.toMillis())))) {
              uniqueUserScores.set(data.userId, {
                id: doc.id,
                userId: data.userId,
                userName: data.userName || 'Anonymous',
                score: data.score,
                timeTaken: typeof data.timeTaken === 'number' ? data.timeTaken : 0,
                completedAt: data.completedAt
              });
            }
          }
        });

        const allLeaderboardEntries = Array.from(uniqueUserScores.values()).sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return (a.timeTaken || Infinity) - (b.timeTaken || Infinity);
        });

        const currentUserEntry = allLeaderboardEntries.find(entry => entry.userId === auth.currentUser?.uid);
        const currentUserRank = currentUserEntry ? allLeaderboardEntries.indexOf(currentUserEntry) + 1 : -1;

        const topPerformers = allLeaderboardEntries.slice(0, 3);
        let displayLeaderboard = [...topPerformers];

        if (currentUserEntry && currentUserRank > 3 && !topPerformers.find(entry => entry.userId === currentUserEntry.userId)) {
            displayLeaderboard.push(currentUserEntry);
        }
        
        const redactUsername = (name, isCurrentUser) => {
            if (isCurrentUser) return name;
            if (!name) return 'Anonymous';

            const visibleChars = 3;
            if (name.length <= visibleChars) return '*'.repeat(name.length);

            if (name.includes('@')) {
                const parts = name.split('@');
                const usernamePart = parts[0];
                const domainPart = parts[1];
                if (usernamePart.length <= visibleChars) return '*'.repeat(usernamePart.length) + '@' + domainPart;
                return usernamePart.substring(0, visibleChars) + '*'.repeat(usernamePart.length - visibleChars) + '@' + domainPart;
            } else {
                 return name.substring(0, visibleChars) + '*'.repeat(name.length - visibleChars);
            }
        };

        setLeaderboard(displayLeaderboard.map((entry, index) => ({
            ...entry,
            userName: redactUsername(entry.userName, entry.userId === auth.currentUser?.uid),
            rank: allLeaderboardEntries.indexOf(entry) + 1
        })));

      } catch (error) {
        console.error('Error fetching leaderboard:', error);
        setError('Failed to load leaderboard');
      } finally {
        setLoading(false);
      }
    };

    if (quizId) {
      fetchLeaderboard();
    }
  }, [quizId]);

  const formatTime = (seconds) => {
    if (typeof seconds !== 'number' || isNaN(seconds) || seconds < 0) {
        return '--m --s';
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'text-green-500';
    if (score >= 50) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg p-3 sm:p-6 max-w-2xl w-full mx-2 sm:mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold flex items-center">
            <FiStar className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-[rgb(130,88,18)]" />
            {quizName} - Top Performers
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FiX className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[rgb(130,88,18)]"></div>
          </div>
        ) : error ? (
          <div className="text-red-500 text-center py-4">{error}</div>
        ) : (
          <div className="space-y-2 sm:space-y-3">
            {leaderboard.length === 0 ? (
              <div className="text-center py-4 text-gray-500 text-sm sm:text-base">
                No entries in leaderboard yet
              </div>
            ) : (
              leaderboard.map((entry, index) => (
                <div 
                  key={entry.id}
                  className={`flex items-center p-2 sm:p-4 rounded-lg ${
                    entry.userId === auth.currentUser?.uid ? 'bg-[rgb(130,88,18)] bg-opacity-10' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex-shrink-0 w-6 sm:w-8 text-center font-bold text-[rgb(130,88,18)] text-sm sm:text-base">
                    #{entry.rank}
                  </div>
                  <div className="flex-grow ml-2 sm:ml-4">
                    <div className="font-semibold text-sm sm:text-base truncate">{entry.userName}</div>
                    <div className="text-xs sm:text-sm text-gray-600">
                      <FiClock className="inline-block w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      {formatTime(entry.timeTaken)}
                    </div>
                  </div>
                  <div className={`flex-shrink-0 font-bold text-sm sm:text-base ${getScoreColor(entry.score)}`}>
                    {entry.score}%
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <div className="mt-4 sm:mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-3 sm:px-4 py-1 sm:py-2 text-sm sm:text-base bg-gray-200 rounded hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default LeaderboardModal; 